import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { NotificationService } from '../services/notification.service';
import { ApiErrorResponse } from '../types/api-error-response';

export const HIDE_ERRORS = new HttpContextToken(() => false);

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  private isRefreshing: boolean = false;
  private tokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(
    private router: Router,
    private authService: AuthService,
    private notificationService: NotificationService,
    private translocoService: TranslocoService,
  ) {}

  t(text: string) {
    return this.translocoService.translate('http-error.' + text);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((err: any) => {
        if (err instanceof HttpErrorResponse) {
          let hideErrors = req.context.get(HIDE_ERRORS);
          if (this.isUnauthorizedOrInvalidGrantError(err) && !req.url.includes('3motionai')) {
            if (!req.url.includes('/token') && this.authService.hasRefreshToken()) {
              return this.refreshTokenAndRetry(req, next);
            } else {
              this.authService.logout(this.getLogoutMessage(err));
              hideErrors = true;
            }
          } else if (this.isForbiddenError(err)) {
            const authToken = this.authService.getAccessToken();
            if (authToken) {
              const payload = JSON.parse(atob(authToken.split('.')[1]));
              if (payload?.['enterprise_ids'] && payload['enterprise_ids'].length === 0) {
                return this.refreshTokenAndRetry(req, next);
              }
            }
          } else if (this.isMaintenanceError(err)) {
            this.router.navigateByUrl('/maintenance');
            return throwError(err);
          }

          if (!hideErrors) {
            this.informUser(err);
          }
        }

        return throwError(err);
      }),
    );
  }

  private isUnauthorizedOrInvalidGrantError(err: HttpErrorResponse): boolean {
    return (
      err.status === HttpStatusCode.Unauthorized ||
      (err.error?.error === 'invalid_grant' && err.error?.error_description?.startsWith('Invalid refresh token'))
    );
  }

  private isForbiddenError(err: HttpErrorResponse): boolean {
    return err.status === HttpStatusCode.Forbidden;
  }

  private informUser(apiErr: ApiErrorResponse): void {
    const userFacingError = this.getUserFacingError(apiErr);

    if (!userFacingError) {
      return;
    }

    this.notificationService.displayError(userFacingError);
  }

  private refreshTokenAndRetry(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.tokenSubject.next(null);
      return this.refreshTokens(req, next);
    }

    return this.tokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((_) => next.handle(req)),
    );
  }

  private refreshTokens(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    return this.authService.refresh().pipe(
      switchMap((success: boolean) => {
        this.isRefreshing = false;

        this.tokenSubject.next(success);

        return next.handle(req);
      }),
      catchError((err) => {
        this.isRefreshing = false;
        this.authService.logout(this.t('Your session has expired. Please log in again to continue.'));

        return throwError(err);
      }),
    );
  }

  private getLogoutMessage(err: ApiErrorResponse): string {
    return (
      this.getUserFacingError(err) ||
      this.t('An error has occurred, please try again. If this problem persists contact support.')
    );
  }

  private getUserFacingError(err: ApiErrorResponse): string {
    if (err.status === HttpStatusCode.Unauthorized) {
      if (err.error?.error_description?.toLowerCase().includes('expired')) {
        return this.t('Your account has been logged out due to inactivity. Please log in again.');
      }

      if (['unauthorized_user', 'access_denied'].includes(err.error?.error) && err.error?.error_description) {
        return err.error.error_description;
      }

      return this.t('Unauthorized request. Please try again later.');
    }

    if (['unauthorized_user', 'access_denied'].includes(err.error?.error) && err.error?.error_description) {
      return err.error.error_description;
    }

    if (err.status === HttpStatusCode.Conflict) {
      if (err.error?.error) {
        return err.error.error;
      }
    }

    if (err.error?.error === 'invalid_grant' && err.error?.error_description?.startsWith('Invalid refresh token')) {
      return this.t('Your session has expired. Please log in again to continue.');
    }

    if (err.status >= 400 && err.status < 500 && err.error?.error) {
      return err.error.error;
    }

    return this.t('An error has occurred, please try again. If this problem persists contact support.');
  }

  private isMaintenanceError(err: HttpErrorResponse): boolean {
    return err.status == 512;
  }
}
